import { z } from 'zod'
import {
  irrigationEquipmentDtoSchema,
  irrigationProblemDtoSchema,
  irrigationTimerDtoSchema,
  waterSourceDtoSchema,
  zoneDtoSchema,
  type IrrigationProblemDto,
  type IrrigationSiteDto,
} from '@lib/dto/irrigation.dto'
import { irrigationSyncOperationSchema } from '@lib/input/irrigation.input'
import logger, { toErrorMessage } from '@/helpers/logger'
import { operationKey } from '@/irrigation-survey/irrigationOperations'
import {
  answerOf,
  sameAnswer,
  withAnswer,
  type ChangeAnswer,
} from '@/site-visits/changeAnswer'
import { isForVisit, type SurveyVisit } from '@/site-visits/surveyVisit'

/*
 * Offline outbox for an irrigation survey: the same design as the tree
 * survey's (`field-map/treeOutbox.ts`, and "Local-first" in
 * docs/field-survey.md), for a different set of records. The phone's copy is
 * what the map draws, and every change is also queued as an intention to
 * replay when the network allows.
 *
 * Its own module and its own storage key rather than a generalization of the
 * tree outbox: the tree queue is hard-wired to trees, crews depend on it, and
 * a refactor that could lose a morning's survey is not a price to pay for
 * sharing forty lines.
 */

/**
 * A queue entry with its display copy left unparsed.
 *
 * The operation is judged on its own. A problem snapshot this build cannot
 * read is dropped afterwards, so it cannot fail the entry: the operation is
 * the only copy of the edit, and the snapshot is only how to draw it until
 * the server answers. A strict snapshot on the operation schema would drop
 * both, which is how a morning's survey used to vanish.
 */
const queuedItemSchema = z.object({
  seq: z.number().int().nonnegative(),
  operation: z.unknown(),
  snapshot: z.unknown().optional(),
})

/** The queue entry as stored, plus the problem as drawn when one survived. */
export interface QueuedOperation {
  seq: number
  operation: z.infer<typeof irrigationSyncOperationSchema>
  /**
   * The problem as the map drew it when this edit was queued. An operation
   * carries only what the API writes (no type name, no who found it). Absent
   * on anything that is not a problem, on a queue written before snapshots,
   * or when the copy could not be read back.
   */
  snapshot?: IrrigationProblemDto
}

export interface IrrigationCache {
  /** What the map draws: server truth with local edits applied. */
  site: IrrigationSiteDto
  queue: QueuedOperation[]
  nextSeq: number
  /**
   * The visit whose problems the working set shows: null for none, and
   * undefined for a cache written before visits existed, whose problems are
   * the site's one migrated visit's. See docs/site-visits.md.
   */
  visitId?: string | null
  /**
   * Whether the problems shown are known to be `visitId`'s: the server's copy
   * of it has been seen, or it was started on this phone and has none yet.
   * Until then no problem is recorded, so a stale view is never sent as this
   * visit's (see the tree outbox's `fresh`).
   */
  fresh?: boolean
}

export function emptySite(siteId: string): IrrigationSiteDto {
  return {
    siteId,
    visitId: null,
    waterSources: [],
    timers: [],
    zones: [],
    equipment: [],
    problems: [],
  }
}

export function emptyCache(siteId: string): IrrigationCache {
  return { site: emptySite(siteId), queue: [], nextSeq: 1 }
}

/** The envelope, validated without looking inside, so each item is judged alone. */
const cacheEnvelopeSchema = z.object({
  site: z
    .object({
      waterSources: z.array(z.unknown()).default([]),
      timers: z.array(z.unknown()).default([]),
      zones: z.array(z.unknown()).default([]),
      equipment: z.array(z.unknown()).default([]),
      problems: z.array(z.unknown()).default([]),
    })
    .default({
      waterSources: [],
      timers: [],
      zones: [],
      equipment: [],
      problems: [],
    }),
  queue: z.array(z.unknown()).default([]),
  nextSeq: z.number().int().nonnegative().optional(),
  visitId: z.string().nullable().optional(),
  fresh: z.boolean().optional(),
})

export interface LoadedCache {
  cache: IrrigationCache
  /**
   * Queued operations that could not be read back: work this phone recorded
   * and can no longer replay. Unreadable drawn records cost nothing, because
   * the server's copy replaces them on the next load; a dropped queue entry
   * is the only copy.
   */
  lostOperations: number
}

function storageKey(siteId: string): string {
  return `swocrm.irrigation.${siteId}.v1`
}

/** Where an unreadable payload is kept, so a person can still recover it by hand. */
function quarantineKey(siteId: string): string {
  return `swocrm.irrigation.${siteId}.v1.unreadable`
}

function quarantine(siteId: string, raw: string): void {
  try {
    window.localStorage.setItem(quarantineKey(siteId), raw)
  } catch (caught) {
    logger.error(
      'irrigation survey: could not quarantine an unreadable cache',
      {
        siteId,
        reason: toErrorMessage(caught),
      },
    )
  }
}

function parseEnvelope(
  raw: string,
): z.infer<typeof cacheEnvelopeSchema> | null {
  try {
    const parsed = cacheEnvelopeSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/**
 * The queue entries this build can replay.
 *
 * A bad snapshot is not a lost operation. The count is entries whose
 * operation could not be read, which is the only copy of that edit.
 */
function parseQueue(
  items: readonly z.infer<typeof cacheEnvelopeSchema>['queue'][number][],
): { kept: QueuedOperation[]; dropped: number } {
  const kept: QueuedOperation[] = []
  let dropped = 0
  for (const item of items) {
    const queued = parseQueued(item)
    if (queued === null) {
      dropped += 1
      continue
    }
    kept.push(queued)
  }
  return { kept, dropped }
}

/** One queue entry, or null when its operation cannot be replayed. */
function parseQueued(
  candidate: z.infer<typeof cacheEnvelopeSchema>['queue'][number],
): QueuedOperation | null {
  const envelope = queuedItemSchema.safeParse(candidate)
  if (!envelope.success) return null
  const operation = irrigationSyncOperationSchema.safeParse(
    envelope.data.operation,
  )
  if (!operation.success) return null
  if (
    operation.data.op !== 'upsertProblem' ||
    envelope.data.snapshot === undefined
  ) {
    return { seq: envelope.data.seq, operation: operation.data }
  }
  const snapshot = irrigationProblemDtoSchema.safeParse(envelope.data.snapshot)
  if (!snapshot.success) {
    return { seq: envelope.data.seq, operation: operation.data }
  }
  return {
    seq: envelope.data.seq,
    operation: operation.data,
    snapshot: snapshot.data,
  }
}

/** The items of one list this build can read; the count it could not. */
function salvage<T>(
  schema: z.ZodType<T>,
  items: readonly z.infer<typeof cacheEnvelopeSchema>['queue'][number][],
): {
  kept: T[]
  dropped: number
} {
  const kept: T[] = []
  for (const item of items) {
    const parsed = schema.safeParse(item)
    if (parsed.success) kept.push(parsed.data)
  }
  return { kept, dropped: items.length - kept.length }
}

/**
 * Read a site's cache, salvaging whatever this build can understand.
 *
 * Each item is parsed on its own, anything unreadable is quarantined rather
 * than dropped, and the number of lost queued operations is returned so the
 * screen can say so out loud instead of showing a tick. The tree outbox
 * learned this the hard way; see its `loadCache`.
 */
export function loadCache(siteId: string): LoadedCache {
  const empty = { cache: emptyCache(siteId), lostOperations: 0 }
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(storageKey(siteId))
  } catch (caught) {
    logger.error('irrigation survey: could not read cache', {
      siteId,
      reason: toErrorMessage(caught),
    })
    return empty
  }
  if (raw === null) return empty
  const envelope = parseEnvelope(raw)
  if (envelope === null) {
    logger.error('irrigation survey: cache is unreadable, quarantining it', {
      siteId,
    })
    quarantine(siteId, raw)
    return empty
  }
  const waterSources = salvage(waterSourceDtoSchema, envelope.site.waterSources)
  const timers = salvage(irrigationTimerDtoSchema, envelope.site.timers)
  const zones = salvage(zoneDtoSchema, envelope.site.zones)
  const equipment = salvage(
    irrigationEquipmentDtoSchema,
    envelope.site.equipment,
  )
  const problems = salvage(irrigationProblemDtoSchema, envelope.site.problems)
  const queue = parseQueue(envelope.queue)
  const droppedRecords =
    waterSources.dropped +
    timers.dropped +
    zones.dropped +
    equipment.dropped +
    problems.dropped
  if (queue.dropped > 0 || droppedRecords > 0) {
    logger.error(
      'irrigation survey: parts of the cache are unreadable, quarantining a copy',
      {
        siteId,
        lostOperations: queue.dropped,
        droppedRecords,
      },
    )
    quarantine(siteId, raw)
  }
  // A sequence number must never come round twice: the flush retires
  // everything at or below a mark, so a reused number could retire an edit
  // made after it.
  const highestSeq = queue.kept.reduce(
    (max, queued) => Math.max(max, queued.seq),
    0,
  )
  return {
    cache: {
      site: {
        siteId,
        visitId: envelope.visitId ?? null,
        waterSources: waterSources.kept,
        timers: timers.kept,
        zones: zones.kept,
        equipment: equipment.kept,
        problems: problems.kept,
      },
      queue: queue.kept,
      nextSeq: Math.max(envelope.nextSeq ?? 1, highestSeq + 1),
      ...(envelope.visitId === undefined ? {} : { visitId: envelope.visitId }),
      ...(envelope.fresh === undefined ? {} : { fresh: envelope.fresh }),
    },
    lostOperations: queue.dropped,
  }
}

export function saveCache(siteId: string, cache: IrrigationCache): void {
  try {
    window.localStorage.setItem(storageKey(siteId), JSON.stringify(cache))
  } catch (caught) {
    logger.error('irrigation survey: could not save cache', {
      siteId,
      reason: toErrorMessage(caught),
    })
  }
}

/** The answer to "Was it wrong, or did it change?" an operation carries. */
function answerOn(
  operation: QueuedOperation['operation'],
): ChangeAnswer | undefined {
  return operation.op === 'upsertProblem' ? undefined : answerOf(operation)
}

/**
 * Append an operation, collapsing any earlier one for the same record.
 *
 * The queue holds intentions, not history: eleven edits to one valve while
 * offline are one statement about what that valve should look like.
 *
 * Except where they were answered differently: a valve moved ("Moved") and
 * then found to have the wrong part ("Was wrong") stays two statements, so
 * the server logs each as what it was. A later edit that asked nothing takes
 * on the earlier one's answer, which an archive never does: it asks its own.
 */
export function enqueue(
  cache: IrrigationCache,
  operation: QueuedOperation['operation'],
  /** The problem as drawn, so a later visit can put this finding back. */
  snapshot?: IrrigationProblemDto,
): IrrigationCache {
  const key = operationKey(operation)
  let answer = answerOn(operation)
  // Newest first, so an unanswered edit takes the latest answer, and stops
  // at a statement answered differently: it and those before it stand.
  const replaced = new Set<number>()
  for (const queued of [...cache.queue].reverse()) {
    if (operationKey(queued.operation) !== key) continue
    const earlier = answerOn(queued.operation)
    if (
      operation.op !== 'archive' &&
      earlier !== undefined &&
      answer !== undefined &&
      !sameAnswer(earlier, answer)
    ) {
      break
    }
    answer ??= earlier
    replaced.add(queued.seq)
  }
  const queue = cache.queue.filter((queued) => !replaced.has(queued.seq))
  const collapsed =
    answer === undefined ||
    operation.op === 'archive' ||
    operation.op === 'upsertProblem'
      ? operation
      : withAnswer(operation, answer)
  // Replacing a finding replaces its picture of it. Anything that is not a
  // problem has no visit-scoped display copy, and a snapshot of a different
  // problem would draw the wrong one when the visit comes back.
  const queued: QueuedOperation =
    snapshot === undefined ||
    collapsed.op !== 'upsertProblem' ||
    snapshot.id !== collapsed.problem.id
      ? { seq: cache.nextSeq, operation: collapsed }
      : { seq: cache.nextSeq, operation: collapsed, snapshot }
  return {
    ...cache,
    queue: [...queue, queued],
    nextSeq: cache.nextSeq + 1,
  }
}

type SiteList = keyof Omit<IrrigationSiteDto, 'siteId' | 'visitId'>

/** Which list of the site a queued operation's record lives in, and its id. */
function locate(operation: QueuedOperation['operation']): {
  list: SiteList
  id: string
} {
  switch (operation.op) {
    case 'upsertWaterSource':
      return { list: 'waterSources', id: operation.waterSource.id }
    case 'upsertTimer':
      return { list: 'timers', id: operation.timer.id }
    case 'upsertZone':
      return { list: 'zones', id: operation.zone.id }
    case 'upsertEquipment':
      return { list: 'equipment', id: operation.equipment.id }
    case 'upsertProblem':
      return { list: 'problems', id: operation.problem.id }
    case 'archive': {
      const list: Record<typeof operation.entity, SiteList> = {
        waterSource: 'waterSources',
        timer: 'timers',
        zone: 'zones',
        equipment: 'equipment',
        problem: 'problems',
      }
      return { list: list[operation.entity], id: operation.id }
    }
  }
}

/** Replace, add or drop one record in one list, without widening its type. */
function layer<T extends { id: string }>(
  server: readonly T[],
  local: readonly T[],
  ops: readonly { id: string; archive: boolean }[],
): T[] {
  const byId = new Map(server.map((item) => [item.id, item]))
  for (const op of ops) {
    if (op.archive) {
      byId.delete(op.id)
      continue
    }
    const record = local.find((item) => item.id === op.id)
    if (record !== undefined) byId.set(record.id, record)
  }
  return [...byId.values()]
}

/**
 * The visit a server answer is showing, when the caller did not name one.
 *
 * Zones and the rest of the system are the site's, so existing callers can
 * keep layering those without a visit. Problems are not: they belong to the
 * visit on the answer, and a missing visit is none.
 */
function shownBy(server: IrrigationSiteDto): SurveyVisit | null {
  if (server.visitId === null) return null
  return { id: server.visitId, joinedIds: [], pending: false }
}

/**
 * Problems to draw for one visit. The server's list, plus queued findings
 * for this visit only.
 *
 * An edit queued for another visit must not be painted on: the operation
 * does not carry the type name or who found it, and the working set may
 * already have been cleared, so the local row is the wrong picture. A
 * snapshot is that picture. A problem with no visit matches only when no
 * visit is open — a missing visit is not "every visit".
 */
function layerProblems(
  server: readonly IrrigationProblemDto[],
  local: readonly IrrigationProblemDto[],
  queue: readonly QueuedOperation[],
  shown: SurveyVisit | null,
): IrrigationProblemDto[] {
  const byId = new Map(server.map((item) => [item.id, item]))
  for (const queued of [...queue].sort((a, b) => a.seq - b.seq)) {
    const { operation } = queued
    if (operation.op === 'archive' && operation.entity === 'problem') {
      byId.delete(operation.id)
      continue
    }
    if (operation.op !== 'upsertProblem') continue
    if (!isForVisit(operation.problem.visitId ?? null, shown)) continue
    const problemId = operation.problem.id
    const record =
      queued.snapshot ?? local.find((item) => item.id === problemId)
    if (record !== undefined) byId.set(record.id, record)
  }
  return [...byId.values()]
}

/**
 * Rebuild the working set from the server's answer plus whatever is still
 * queued. The server is authoritative for what it knows, including desk edits
 * this phone never saw; operations recorded while a request was in flight
 * are layered back on top. `local` supplies the full record for a queued
 * upsert, because an operation carries only what the API writes.
 *
 * `shown` chooses which queued problems belong on screen. Left out, it is
 * the visit on the server's answer, which is what a caller that only layers
 * zones is asking for.
 */
export function applyQueue(
  server: IrrigationSiteDto,
  queue: readonly QueuedOperation[],
  local: IrrigationSiteDto,
  shown?: SurveyVisit | null,
): IrrigationSiteDto {
  const shownVisit = shown === undefined ? shownBy(server) : shown
  const opsFor: Record<
    Exclude<SiteList, 'problems'>,
    { id: string; archive: boolean }[]
  > = {
    waterSources: [],
    timers: [],
    zones: [],
    equipment: [],
  }
  for (const { operation } of [...queue].sort((a, b) => a.seq - b.seq)) {
    if (
      operation.op === 'upsertProblem' ||
      (operation.op === 'archive' && operation.entity === 'problem')
    ) {
      continue
    }
    const { list, id } = locate(operation)
    if (list === 'problems') continue
    opsFor[list].push({ id, archive: operation.op === 'archive' })
  }
  return {
    siteId: server.siteId,
    visitId: server.visitId,
    waterSources: layer(
      server.waterSources,
      local.waterSources,
      opsFor.waterSources,
    ),
    timers: layer(server.timers, local.timers, opsFor.timers),
    zones: layer(server.zones, local.zones, opsFor.zones),
    equipment: layer(server.equipment, local.equipment, opsFor.equipment),
    problems: layerProblems(
      server.problems,
      local.problems,
      queue,
      shownVisit,
    ),
  }
}

/**
 * Copy a queued problem onto the operation before the working set is cleared.
 *
 * Only from a working set that is still that visit's. After a visit change
 * the problems on screen are that other visit's, and copying one would
 * freeze the wrong finding — or an empty list — as what was recorded. An
 * edit that already has a snapshot keeps it.
 */
function rememberProblemSnapshots(cache: IrrigationCache): IrrigationCache {
  let changed = false
  const queue = cache.queue.map((queued) => {
    if (queued.operation.op !== 'upsertProblem' || queued.snapshot !== undefined) {
      return queued
    }
    const recordedOn = queued.operation.problem.visitId ?? null
    if (cache.visitId !== undefined && (cache.visitId ?? null) !== recordedOn) {
      return queued
    }
    const problemId = queued.operation.problem.id
    const problem = cache.site.problems.find((item) => item.id === problemId)
    if (problem === undefined) return queued
    changed = true
    return { ...queued, snapshot: problem }
  })
  if (!changed) return cache
  return { ...cache, queue }
}

/**
 * Problems queued for one visit, in the order they were recorded.
 *
 * Starts empty on purpose. The previous visit's list is not a starting
 * point: those findings belong to it. A queued finding is put back from its
 * snapshot, which is what makes switching back — including while offline,
 * before the server has answered — show the problem again instead of
 * inviting a second one.
 */
function problemsForVisit(
  cache: IrrigationCache,
  visit: SurveyVisit | null,
): IrrigationProblemDto[] {
  const byId = new Map<string, IrrigationProblemDto>()
  for (const queued of [...cache.queue].sort((a, b) => a.seq - b.seq)) {
    const { operation } = queued
    if (operation.op === 'archive' && operation.entity === 'problem') {
      const visitId = operation.visitId ?? null
      if (visitId === null || isForVisit(visitId, visit)) byId.delete(operation.id)
      continue
    }
    if (operation.op !== 'upsertProblem') continue
    if (!isForVisit(operation.problem.visitId ?? null, visit)) continue
    const problemId = operation.problem.id
    const record =
      queued.snapshot ??
      cache.site.problems.find((item) => item.id === problemId)
    if (record !== undefined) byId.set(record.id, record)
  }
  return [...byId.values()]
}

/**
 * The cache as it should read in a visit: unchanged when it already shows
 * that visit, relabeled when it shows one joined into it, and otherwise with
 * only this visit's queued problems and not fresh until the server's copy
 * comes back (fresh at once when `pending`: this phone started it, so it has
 * none yet). The rest of the system is the site's and stays.
 *
 * Queued findings are kept, and each one remembers the problem it was made
 * against. Clearing the list must not forget them, or an offline phone shows
 * an empty visit and the next tap records a duplicate. Switching back puts
 * those problems on screen again.
 *
 * A cache from before visits keeps its problems but is not fresh, as in the
 * tree outbox's `inVisit`.
 */
export function inVisit(
  cache: IrrigationCache,
  visit: SurveyVisit | null,
): IrrigationCache {
  const remembered = rememberProblemSnapshots(cache)
  const shown = remembered.visitId
  const next = visit?.id ?? null
  if (shown === next) return remembered
  const joined =
    visit !== null && shown != null && visit.joinedIds.includes(shown)
  const legacy = shown === undefined
  return {
    ...remembered,
    visitId: next,
    ...(joined ? {} : { fresh: legacy ? false : visit?.pending === true }),
    site: {
      ...remembered.site,
      visitId: next,
      problems:
        joined || legacy
          ? remembered.site.problems
          : problemsForVisit(remembered, visit),
    },
  }
}
